import React, { useEffect, useState } from 'react';
import Panel from '../../components/ui/Panel';
import ComponentShowcase from '../../components/ui/ComponentShowcase';
import ProgressBar from '../../components/ui/ProgressBar';

const basicCode = `<ProgressBar value={65} max={100} label="Hitpoints" />`;
const variantsCode = `<ProgressBar value={75} variant="default" label="Experience" />
<ProgressBar value={75} variant="green" label="Hitpoints" />
<ProgressBar value={75} variant="red" label="Low health" />
<ProgressBar value={75} variant="blue" label="Prayer" />`;
const sizesCode = `<ProgressBar value={75} size="small" />
<ProgressBar value={75} size="medium" />
<ProgressBar value={75} size="large" />`;
const labelsCode = `<ProgressBar value={65} label="Hitpoints" showPercentage />`;

const ProgressBarPage: React.FC = () => {
  const [animated, setAnimated] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimated((current) => (current >= 100 ? 0 : current + 1));
    }, 50);
    return () => clearInterval(interval);
  }, []);

  return (
    <div>
      <h1 id="overview" className="text-3xl text-osrs-gold font-bold uppercase mb-4">Progress Bar</h1>
      <p className="text-osrs-yellow mb-8">
        Hitpoints fill green. Prayer fills blue. Experience fills gold. The track is the same inset used by inputs.
      </p>

      <section id="examples" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Examples</h2>
        <ComponentShowcase
          id="basic"
          title="Hitpoints"
          component={<div className="w-full max-w-md"><ProgressBar value={65} max={100} label="Hitpoints" variant="green" /></div>}
          code={basicCode}
        />
        <ComponentShowcase
          id="variants"
          title="Variants"
          description="Gold, green, red, and blue fills."
          component={
            <div className="w-full max-w-md space-y-4">
              <ProgressBar value={75} variant="default" label="Experience" />
              <ProgressBar value={75} variant="green" label="Hitpoints" />
              <ProgressBar value={75} variant="red" label="Low health" />
              <ProgressBar value={75} variant="blue" label="Prayer" />
            </div>
          }
          code={variantsCode}
        />
        <ComponentShowcase
          id="sizes"
          title="Sizes"
          component={
            <div className="w-full max-w-md space-y-4">
              <ProgressBar value={75} size="small" variant="green" />
              <ProgressBar value={75} size="medium" variant="green" />
              <ProgressBar value={75} size="large" variant="green" />
            </div>
          }
          code={sizesCode}
        />
        <ComponentShowcase
          id="labels"
          title="Percentage"
          component={<div className="w-full max-w-md"><ProgressBar value={65} label="Hitpoints" variant="green" showPercentage /></div>}
          code={labelsCode}
        />
        <ComponentShowcase
          id="animated"
          title="Changing value"
          component={<div className="w-full max-w-md"><ProgressBar value={animated} label="Cooking" variant="default" showPercentage /></div>}
          code={`<ProgressBar value={progress} label="Cooking" />`}
        />
      </section>

      <section id="usage-guidelines" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Usage Guidelines</h2>
        <Panel>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Use green for hitpoints and red when the value is a warning.</li>
            <li>Use blue for prayer and gold for experience or a skill action.</li>
            <li>Pass a label so the bar has a name.</li>
          </ul>
        </Panel>
      </section>

      <section id="api-reference" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">API Reference</h2>
        <Panel>
          <table className="w-full text-left text-osrs-yellow">
            <thead>
              <tr className="text-osrs-gold">
                <th className="py-2 px-4">Prop</th>
                <th className="py-2 px-4">Type</th>
                <th className="py-2 px-4">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-2 px-4">value</td>
                <td className="py-2 px-4">number</td>
                <td className="py-2 px-4">Current amount</td>
              </tr>
              <tr>
                <td className="py-2 px-4">max</td>
                <td className="py-2 px-4">number</td>
                <td className="py-2 px-4">Full amount. Default 100.</td>
              </tr>
              <tr>
                <td className="py-2 px-4">variant</td>
                <td className="py-2 px-4">default | green | red | blue</td>
                <td className="py-2 px-4">Fill colour</td>
              </tr>
              <tr>
                <td className="py-2 px-4">size</td>
                <td className="py-2 px-4">small | medium | large</td>
                <td className="py-2 px-4">Track height</td>
              </tr>
              <tr>
                <td className="py-2 px-4">showPercentage</td>
                <td className="py-2 px-4">boolean</td>
                <td className="py-2 px-4">Prints the percent under the bar</td>
              </tr>
            </tbody>
          </table>
        </Panel>
      </section>
    </div>
  );
};

export default ProgressBarPage;
